import { fetchJson } from "../lib/api";

export type ArticleIdentityReviewSource =
  | "weak_mfr"
  | "ean_risk"
  | "generic_sht";

export type ArticleIdentityReviewStatus = "open" | "confirmed" | "rejected";

export type ArticleIdentityReviewDecisionAction = "confirm" | "reject" | "reset";

export type ArticleIdentityReviewCounts = Record<string, number>;

export type ArticleIdentityReviewSummary = {
  total_rows: number;
  counts: Record<string, ArticleIdentityReviewCounts>;
};

export type ArticleIdentityReviewRow = {
  customer_id: string;
  run_id: string;
  review_id: string;
  review_source: ArticleIdentityReviewSource | string;
  review_type: string;
  review_status: ArticleIdentityReviewStatus | string;
  review_decision: string;
  review_note: string;
  review_bucket: string;
  diagnostic_status: string;
  source_review_status: string;
  article_group_key?: string;
  article_group_key_samples?: string;
  article_code_samples?: string;
  candidate_id?: string;
  normalized_manufacturer_article_code?: string;
  normalized_ean?: string;
  risk_reasons?: string;
  suppliers?: string;
  gc_article_group_key?: string;
  gc_article_code?: string;
  gc_uom?: string;
  gc_product_group?: string;
  sht_article_group_key?: string;
  sht_article_code?: string;
  sht_uom?: string;
  sht_product_group?: string;
  manufacturer_name_samples?: string;
  manufacturer_name_count?: string;
  manufacturer_article_code_samples?: string;
  product_group_samples?: string;
  review_reason?: string;
  same_normalized_uom?: string;
  decision_event_id?: string;
  decision_actor_user_id?: string;
  decision_created_at?: string;
};

export type ArticleIdentityReviewRowsResponse = {
  customer_id: string;
  run_id: string;
  review_source: ArticleIdentityReviewSource | "all" | string;
  total: number;
  source_total: number;
  limit: number;
  offset: number;
  summary: ArticleIdentityReviewSummary;
  filtered_summary: ArticleIdentityReviewSummary;
  items: ArticleIdentityReviewRow[];
};

export type ArticleIdentityReviewSummaryResponse = {
  customer_id: string;
  run_id: string;
  total_rows: number;
  decision_event_count: number;
  summary: ArticleIdentityReviewSummary;
};

export type ArticleIdentityReviewRowsParams = {
  source?: ArticleIdentityReviewSource | null;
  status?: string | null;
  search?: string | null;
  limit?: number;
  offset?: number;
};

export async function fetchArticleIdentityReviewSummary(): Promise<ArticleIdentityReviewSummaryResponse> {
  return fetchJson<ArticleIdentityReviewSummaryResponse>(
    "/api/reconciliation/article-identity-review/current/summary",
  );
}

export async function fetchArticleIdentityReviewRows(
  args: ArticleIdentityReviewRowsParams = {},
): Promise<ArticleIdentityReviewRowsResponse> {
  const params = new URLSearchParams();
  if (args.source) params.set("source", args.source);
  if (args.status && args.status !== "all") params.set("status", args.status);
  if (args.search?.trim()) params.set("search", args.search.trim());
  params.set("limit", String(args.limit ?? 100));
  params.set("offset", String(args.offset ?? 0));
  return fetchJson<ArticleIdentityReviewRowsResponse>(
    `/api/reconciliation/article-identity-review/current/rows?${params.toString()}`,
  );
}

export async function decideArticleIdentityReviewRow(args: {
  reviewId: string;
  action: ArticleIdentityReviewDecisionAction;
  reason?: string | null;
}): Promise<{
  row: ArticleIdentityReviewRow;
  decision: Record<string, unknown>;
  user_id: string;
  customer_id: string;
  run_id: string;
}> {
  return fetchJson(
    "/api/reconciliation/article-identity-review/current/decisions",
    {
      method: "POST",
      body: JSON.stringify({
        review_id: args.reviewId,
        action: args.action,
        reason: args.reason ?? null,
      }),
    },
  );
}
